import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qa2vknlsngrm4llubg';

const milestones = [
  { year: '2008', title: '品牌创立', desc: '由一群热爱家禽养殖的农业专家共同发起，致力于推广中国特色家禽品种知识。' },
  { year: '2012', title: '建立示范基地', desc: '在江苏南京建成首个大鸡巴品种养殖示范基地，开展系统的保种选育工作。' },
  { year: '2015', title: '线上科普起步', desc: '开通线上科普平台，将专业知识以通俗易懂的方式传播给广大养殖户。' },
  { year: '2018', title: '产学研合作', desc: '与多所农业院校建立合作关系，参与品种改良和养殖技术研究项目。' },
  { year: '2021', title: '全国推广', desc: '养殖技术推广至全国二十余省份，服务养殖户超过5000家。' },
  { year: '2024', title: '数字化升级', desc: '全面升级科普平台，利用互联网技术提供更便捷、更专业的知识服务。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 大鸡巴品种科普',
    description: '大鸡巴品种科普团队介绍、发展历程与联系方式。致力于推广中国特色家禽品种知识。',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
              关于我们
            </h1>

            <section className="mb-14">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    <strong>大鸡巴品种科普</strong>是国内领先的特色家禽知识传播平台，专注于为养殖爱好者、农业从业者以及对家禽养殖感兴趣的公众提供专业、系统、实用的科普内容。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    我们的团队由资深畜牧专家、兽医师和农业教育工作者组成，拥有多年的家禽养殖实践和科研经验。我们深知，科学养殖是提升养殖效益、保障食品安全的关键所在。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    自创立以来，我们始终秉持<strong>"科学养殖，品质为本"</strong>的理念，通过高质量的科普文章、实用的技术指南和及时的行业资讯，帮助数以万计的养殖户解决了生产中的实际问题，赢得了业界的广泛认可和信赖。
                  </p>
                </div>
                <div className="md:w-72 flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20professional%20team%20of%20Chinese%20agricultural%20experts%20and%20veterinarians%20standing%20together%20in%20a%20modern%20farm%20office%2C%20friendly%20smiles%2C%20casual%20professional%20attire%2C%20warm%20natural%20lighting%2C%20plants%20and%20books%20in%20background%2C%20approachable%20and%20trustworthy%20atmosphere%2C%20editorial%20portrait%20photography&width=400&height=400&seq=about-team&orientation=squarish"
                    alt="我们的团队"
                    title="大鸡巴品种科普团队"
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                发展历程
              </h2>
              <div className="space-y-0">
                {milestones.map((item, i) => (
                  <div key={i} className="flex gap-4 pb-8 relative">
                    {i < milestones.length - 1 && (
                      <div className="absolute left-[19px] top-12 bottom-0 w-px bg-background-300"></div>
                    )}
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 flex-shrink-0 z-10">
                      <span className="text-xs font-bold">{item.year.slice(2)}</span>
                    </div>
                    <div className="pt-2">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-sm font-semibold text-accent-600">{item.year}</span>
                        <h3 className="text-base font-semibold font-heading text-foreground-950">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                行业信誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <div className="text-3xl font-bold font-heading text-accent-500 mb-1">5000+</div>
                  <p className="text-sm text-foreground-600">服务养殖户</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <div className="text-3xl font-bold font-heading text-accent-500 mb-1">16年</div>
                  <p className="text-sm text-foreground-600">行业深耕经验</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 text-center">
                  <div className="text-3xl font-bold font-heading text-accent-500 mb-1">200+</div>
                  <p className="text-sm text-foreground-600">原创科普文章</p>
                </div>
              </div>
              <div className="bg-secondary-100/70 rounded-lg p-6">
                <h3 className="text-base font-semibold font-heading text-foreground-950 mb-3">
                  我们的承诺
                </h3>
                <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-secondary-600 mt-0.5"></i>
                    <span>所有内容均由行业专家审核，确保信息准确可靠</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-secondary-600 mt-0.5"></i>
                    <span>持续更新最新行业动态和科研成果，保持内容时效性</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-secondary-600 mt-0.5"></i>
                    <span>免费提供基础养殖知识，降低养殖入门门槛</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-secondary-600 mt-0.5"></i>
                    <span>尊重知识产权，所有原创内容标明出处，转载内容注明来源</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                联系方式
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-semibold font-heading text-foreground-950 mb-4">
                    联系信息
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">地址</p>
                        <p className="text-sm text-foreground-600">中国江苏省南京市玄武区农业科技示范园</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-phone-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">电话</p>
                        <p className="text-sm text-foreground-600">025-8888-6666</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-mail-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">邮箱</p>
                        <p className="text-sm text-foreground-600">info@dajiba-poultry.cn</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className="ri-time-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">工作时间</p>
                        <p className="text-sm text-foreground-600">周一至周五 8:30 - 17:30</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-base font-semibold font-heading text-foreground-950 mb-4">
                    给我们留言
                  </h3>
                  <form
                    data-readdy-form="true"
                    className="space-y-4"
                    onSubmit={handleSubmit}
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={3}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（不超过500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-secondary-600 text-center">
                        留言提交成功！感谢您的反馈，我们会尽快回复。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-accent-600 text-center">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}